/*
 * @Author: Ryan
 * @Date: 2026-02-06
 * @Description: 视频翻译 Landing 页面 - Spanish to Japanese
 */
import type { GetStaticProps } from 'next';
import dynamic from 'next/dynamic';
import React from 'react';

import { withLandingLayout } from '@/landing/components/LandingLayout';
import SEOHead, { type SEOHeadProps } from '@/landing/components/SEOHead';
import type { FAQItem } from '@/landing/components/templates/FAQSection';
import type { FeatureItem } from '@/landing/components/templates/FeaturesSection';
import HeroSection from '@/landing/components/templates/HeroSection';
import type { StepData } from '@/landing/components/templates/HowToSection';
import type { UseCaseItem } from '@/landing/components/templates/UseCasesSection';
import type { ReasonItem } from '@/landing/components/templates/WhyChooseSection';
import {
  createBreadcrumbSchema,
  createFAQSchema,
  createOrganizationSchema,
  createSoftwareAppSchema,
} from '@/landing/utils/structuredData';

// 非首屏组件懒加载（代码拆分，减少首屏 JS 体积）
const VideoTranslationDemos = dynamic(
  () => import('@/landing/components/templates/VideoTranslationDemos'),
);
const FeaturesSection = dynamic(
  () => import('@/landing/components/templates/FeaturesSection'),
);
const HowToSection = dynamic(() => import('@/landing/components/templates/HowToSection'));
const UseCasesSection = dynamic(
  () => import('@/landing/components/templates/UseCasesSection'),
);
const LanguageListSection = dynamic(
  () => import('@/landing/components/templates/LanguageListSection'),
);
const WhyChooseSection = dynamic(
  () => import('@/landing/components/templates/WhyChooseSection'),
);
const VsSection = dynamic(() => import('@/landing/components/templates/VsSection'));
const ExploreToolsSection = dynamic(
  () => import('@/landing/components/templates/ExploreToolsSection'),
);
const FAQSection = dynamic(() => import('@/landing/components/templates/FAQSection'));
const FooterCTASection = dynamic(
  () => import('@/landing/components/templates/FooterCTASection'),
);

// ============ Static Content - Spanish to Japanese ============

const PAGE_URL = 'https://falcocut.ai/translate-video-from-spanish-to-japanese';

// Hero Section
const HERO_CONTENT = {
  title: 'Translate Spanish Video to Japanese',
  subtitle:
    'Upload your Spanish video in FalcoCut and instantly convert it into natural\u2011sounding Japanese. Our AI handles dubbing, subtitles, lip sync, and translation for you. Create high\u2011quality multilingual content with just a few clicks.',
  cta: 'Upload File',
  sourceLanguage: 'Spanish',
  targetLanguage: 'Japanese',
};

// Video Translation Demos
const DEMOS_CONTENT = {
  title: 'Watch Your Video Turn Into a Fully Translated Version',
  subtitle:
    'FalcoCut presents fully translated videos that keeps your message sharp and easy to understand in any language. Enjoy fast turnaround and consistent quality for product demos, ads, and promotional content.',
  cta: 'Translate Spanish Video',
};

// Features Section
const FEATURES_CONTENT = {
  title: 'Best Spanish-to-Japanese Video Translator for Every Campaign',
  subtitle:
    'FalcoCut gives marketers, sellers, and creative teams a fast, reliable way to produce polished translated Spanish videos. Every feature is designed to help you deliver clear, professional content without slowing down your workflow.',
  cta: 'Translate Spanish Video',
};

const FEATURE_IMAGES = [
  'https://assets.falcocut.ai/tools/video-translator/translate-into-different-languages-instantly.webp',
  'https://assets.falcocut.ai/tools/video-translator/extensive-library-of-authentic-ai-voices.webp',
  'https://assets.falcocut.ai/tools/video-translator/ai-voice-cloning-and-natural-dubbing.webp',
  'https://assets.falcocut.ai/tools/video-translator/accurate-subtitles-with-editing-options.webp',
  'https://assets.falcocut.ai/tools/video-translator/seamless-lip-sync-for-realism.webp',
  'https://assets.falcocut.ai/tools/video-translator/all-in-one-video-localization-platform.webp',
];

const FEATURES: FeatureItem[] = [
  {
    title: 'Accurate Translation with Multilingual Supports',
    description:
      'FalcoCut combines advanced speech recognition with AI translation to deliver clear, context\u2011aware results across more than 30 supported languages, such as Spanish, German, Chinese and Japanese. It helps your videos stay accurate and consistent, even when handling complex product details or fast\u2011paced dialogue. The system also produces results far faster than traditional workflows, helping teams meet tight deadlines without losing quality. Viewers stay engaged because the translated message remains easy to understand across different markets. Your content becomes more effective, more scalable, and more ready for commercial use across global channels.',
    imageUrl: FEATURE_IMAGES[0],
    imageAlt: 'Accurate Translation with Multilingual Supports',
  },
  {
    title: 'Natural Voiceovers That Match Your Brand Style',
    description:
      'Every brand needs audio that feels real, and FalcoCut delivers more than 100 voices that sound natural, smooth, and trustworthy. These voice options support a wide range of tones and styles, giving teams the freedom to match their brand identity across campaigns. This variety makes it easier to create unified ads, demos, and social posts without extra production work. Japanese viewers often respond better to audio that sounds real and easy to understand. With this flexibility, creators can shape the right mood for any project while keeping every translated video polished and ready for commercial use.',
    imageUrl: FEATURE_IMAGES[1],
    imageAlt: 'Natural Voiceovers That Match Your Brand Style',
  },
  {
    title: 'Flexible Voice Translation & AI Dubbing',
    description:
      'FalcoCut uses advanced AI voice cloning to recreate your original Spanish video with clear, expressive audio that carries your tone into Japanese. It can also generate a fully AI\u2011dubbed Japanese voice using high\u2011quality voice models for a more authentic result. Global marketing teams can keep their brand personality consistent across markets while ensuring every video maintains the same tone, pacing, and emotional style across languages. You can also keep your original Spanish voice and layer a Japanese translation on top, giving you more flexibility for different formats and platforms. With these options, your translated videos stay engaging, recognizable, and ready for global campaigns.',
    imageUrl: FEATURE_IMAGES[2],
    imageAlt: 'Flexible Voice Translation & AI Dubbing',
  },
  {
    title: 'Smart Subtitles for Clear, Accessible Messaging',
    description:
      'When your video shifts from Spanish to Japanese, clear subtitles will help viewers stay engaged longer by making every message easier to read and understand. FalcoCut supports multiple display modes, including bilingual and translated\u2011only subtitles, so teams can choose the format that fits their campaign goals. You can select from a wide range of subtitle styles that match your brand look, from simple text to bold, high\u2011contrast designs. Our system can even translate based on an uploaded original subtitle file, helping you keep accuracy high when working with detailed scripts. With FalcoCut, your videos become easier to publish, repurpose, and share across platforms that rely on clear and accessible communication.',
    imageUrl: FEATURE_IMAGES[3],
    imageAlt: 'Smart Subtitles for Clear, Accessible Messaging',
  },
  {
    title: 'Advanced Lip Sync for Smooth Delivery',
    description:
      'Lip sync technology in FalcoCut aligns translated audio with on\u2011screen speech so your Spanish\u2011to\u2011Japanese videos look natural and believable. This precise matching helps your content feel more polished and reduces the awkward timing issues that can break immersion. Brands that rely on product showcases, ads, or creator\u2011led content gain a more professional result that supports clear communication. The synchronized delivery also keeps your message strong by preserving expression and rhythm across languages. Teams can publish global versions without extra manual fixes or complex editing steps. With this level of realism, your translated videos appear unified, smooth, and ready for publishing widely.',
    imageUrl: FEATURE_IMAGES[4],
    imageAlt: 'Advanced Lip Sync for Smooth Delivery',
  },
  {
    title: 'All\u2011in\u2011One Workspace for Global Video Translation',
    description:
      'FalcoCut brings video translation, dubbing, subtitles, and lip sync into one organized workspace that helps teams move faster across many types of content, including UGC ads, product walk\u2011throughs, interviews, and promotional clips. By keeping every localization tool in a single place, the platform removes the need to switch between different apps or services, shortens production time and reduces the delays that often slow down global campaigns. Quality stays consistent because FalcoCut applies the same translation and processing standards across every supported language. With fewer external tools to manage, teams save both time and cost while producing global\u2011ready content at scale.',
    imageUrl: FEATURE_IMAGES[5],
    imageAlt: 'All-in-One Workspace for Global Video Translation',
  },
];

// How To Section
const HOW_TO_CONTENT = {
  title: 'How to Translate Spanish Video to Japanese in FalcoCut',
  cta: 'Start Translating',
};

const HOW_TO_STEPS: StepData[] = [
  {
    number: 1,
    title: 'Step 1: Upload Your Spanish Video',
    description:
      'Upload an MP4 or MOV from your device, or pick a file already saved in your FalcoCut library. The platform prepares a preview so you can check the content before translating.',
  },
  {
    number: 2,
    title: 'Step 2: Set Source and Target Languages',
    description:
      'Choose the original language of your video, then select the language you want to translate into. FalcoCut supports up to 2 target languages.',
  },
  {
    number: 3,
    title: 'Step 3: Customize Subtitles and Voice',
    description:
      'Turn on subtitle translation to capture speech, then choose whether you want bilingual or translated\u2011only subtitles. If you prefer dubbing, select an AI voice or keep your original voice through voice cloning, and enable lip sync for a natural result.',
  },
  {
    number: 4,
    title: 'Step 4: Save and Share Your Video',
    description:
      'Pick your subtitle style and confirm the final preview. When everything looks right, export your translated video and use it across your marketing channels, social platforms, or internal projects.',
  },
];

// Use Cases Section
const USE_CASES_CONTENT = {
  title: 'Powerful Ways Teams Use FalcoCut for Translation',
};

const USE_CASES: UseCaseItem[] = [
  {
    title: 'E\u2011Commerce',
    description:
      'FalcoCut helps online sellers translate product videos into multiple languages, making listings easier to understand for global shoppers. Natural dubbing and multilingual support help build trust and boosts engagement on marketplaces where customers expect localized content.',
  },
  {
    title: 'Global Education',
    description:
      'Educators can adapt lectures, tutorials, and training videos for international learners with accurate subtitles, uploaded glossaries, and natural dubbing. These make lessons easier to understand for students who rely on reading or need clearer language support.',
  },
  {
    title: 'Social Creators',
    description:
      'Creators can expand their reach by translating short\u2011form videos into new languages without re\u2011recording anything. AI dubbing and lip sync helps maintain personality and tone across global audiences. Subtitles also improve watch time on platforms where many people view content without sound.',
  },
  {
    title: 'Business Marketing',
    description:
      'Marketing teams rely on FalcoCut to localize campaign videos quickly and consistently across regions. Multilingual subtitles, lip\u2011synced translations, and polished dubbing help brands deliver clear messages that resonate with international customers.',
  },
  {
    title: 'Event Collaboration',
    description:
      'Event organizers can translate keynote clips, panel discussions, and recap videos to reach attendees worldwide and share event highlights without extra production work. Participants who prefer reading or have hearing needs can get accessibility supports from various subtitles.',
  },
  {
    title: 'Media & Podcasts',
    description:
      'Media teams and podcasters use FalcoCut to translate interviews, commentary, and storytelling videos for global audiences with natural AI voices. Clear and localized subtitles make it easier to improve discovery on platforms that index text for search visibility.',
  },
];

// Language List Section
const LANGUAGE_LIST_CONTENT = {
  title: 'Reach Global Audiences with Fast AI Translation',
  subtitle:
    'FalcoCut makes video translation simple by supporting 35 languages across every type of content. Anyone can upload a video and get smooth, accurate results.',
  cta: 'Translate Spanish Video',
};

// Why Choose Section
const WHY_CHOOSE_CONTENT = {
  title: 'Your Trusted Platform for Fast, Accurate Video Translation',
  subtitle:
    'FalcoCut gives teams a reliable way to translate Spanish videos into Japanese quickly without technical steps or complicated workflows. Everything runs in your browser, saving time and keeping your content secure from upload to export.',
  cta: 'Translate Spanish Video to Japanese Now',
};

const WHY_CHOOSE_REASONS: ReasonItem[] = [
  {
    title: 'Simple Workflow',
    description:
      'Run the entire translation process directly in your browser without complex setup and you can start working immediately. Even first\u2011time users can translate videos confidently.',
  },
  {
    title: 'Lightning\u2011Quick Processing',
    description:
      "Accelerate your production timelines with FalcoCut's fast translation speeds that prevent delays across global campaigns. You get polished results without long waiting times.",
  },
  {
    title: 'Various Editing Tools',
    description:
      'Adapt your videos with subtitles, dubbing, voice translation, and lip sync that support any content style. Reach diverse audiences easily by choosing the translation approach that best matches your goals.',
  },
  {
    title: 'High\u2011Accuracy Results',
    description:
      'Maintain clarity across languages with accurate subtitles and natural\u2011sounding voice output. Protect your message by ensuring translations stay consistent and dependable in every region.',
  },
  {
    title: 'High Value, Low Cost',
    description:
      'Reduce localization expenses by using one platform that replaces multiple translation tools and services. Support teams of any size by making global\u2011ready content more affordable and scalable.',
  },
  {
    title: 'Security You Can Rely On',
    description:
      'Protect your videos with controlled handling throughout the translation process. Build confidence across teams by ensuring sensitive content remains secure from start to finish.',
  },
];

// VS Section
const VS_CONTENT = {
  title: 'Old Way vs. FalcoCut AI Video Translation',
  subtitle:
    'FalcoCut gives you a faster, more scalable, and more affordable alternative to manual video translation, helping you produce polished multilingual content in minutes.',
  oldTitle: 'Old Way',
  oldPoints: [
    'Turnaround takes days or weeks',
    'Costs 5\u201110\u00d7 more per video',
    'Quality varies between translators',
    'Requires heavy manual editing',
    'Hard to scale beyond a few videos',
  ],
  oldConclusion: 'Manual translation drains time, budget, and momentum.',
  newTitle: 'FalcoCut AI Video Translation',
  newPoints: [
    'Delivers translated results in minutes',
    'Cuts costs by over 70%',
    'Automates 80% of the workflow',
    'Keeps quality consistent',
    'Supports 30+ languages instantly',
  ],
  newConclusion: 'FalcoCut speeds up production and lowers costs at scale.',
  cta: 'Translate Spanish Video',
};

// FAQ Section
const FAQ_CONTENT = {
  title: 'Frequently Asked Questions',
};

const FAQS: FAQItem[] = [
  {
    question: 'How do I translate a Spanish video into Japanese?',
    answer:
      'Step 1: Upload your video to FalcoCut. Step 2: Choose Spanish as the source language and select Japanese as the target language. Step 3: Pick your preferred translation options - subtitles, dubbing, or both depending on your project needs. Let the system automatically transcribe the audio and generate accurate Japanese output. Step 4: Review the results directly in your browser before exporting the final version. Complete the entire workflow without switching tools or performing manual steps.',
  },
  {
    question: 'How does Spanish\u2011to\u2011Japanese video translation work?',
    answer:
      'FalcoCut first converts the Spanish audio into text using advanced speech recognition. The system then translates the transcript into Japanese with consistent, high\u2011quality output. You can enable subtitles, dubbing, or lip sync depending on your preferred format. Each step is automated to reduce manual editing and speed up production. The final result is processed quickly and ready for export.',
  },
  {
    question: 'Do I need transcription before translation?',
    answer:
      'Yes. FalcoCut automatically transcribes the Spanish audio before generating the Japanese translation. This ensures accurate timing for subtitles and smooth alignment for dubbing. You do not need to prepare or upload any transcript manually. The entire transcription step happens automatically inside the workflow.',
  },
  {
    question: 'How long does video translation usually take?',
    answer:
      'FalcoCut processes most videos within minutes depending on length and audio clarity. Shorter clips finish quickly, while longer videos require slightly more time. The automated workflow eliminates delays common in manual translation processes. You can expect consistent performance across different project types. Most creators receive their translated output far faster than traditional methods.',
  },
  {
    question: 'Can I translate audio files instead of video?',
    answer:
      'No. FalcoCut is designed specifically for video translation and does not support standalone audio files currently. The system requires a video format to generate subtitles, dubbing, or lip sync correctly. Audio\u2011only uploads cannot be processed because the workflow depends on video timing and structure. You will need to convert your audio into a video file before uploading. This ensures the platform can deliver accurate and properly aligned translation results.',
  },
  {
    question: 'Which video formats can I upload?',
    answer:
      'FalcoCut supports widely used video formats like MP4 and MOV for smooth uploading and reliable processing. These formats ensure stable transcription and accurate translation throughout the workflow. You can upload your existing video files without converting them into different formats. The platform is optimized to handle common video standards used across major devices. Audio\u2011only formats are not supported, so please upload a video file for translation.',
  },
  {
    question: 'Which languages are supported besides Spanish and Japanese?',
    answer:
      'FalcoCut supports over 35 languages for both subtitles and dubbing. You can translate videos into multiple target languages, like Chinese, English, Arabic, Hindi, and Italian, within the same workflow. The platform is built for creators publishing across global regions. A full list of supported languages is available directly inside the tool. This flexibility helps teams reach wider audiences with minimal effort.',
  },
  {
    question: "What's the difference between dubbing and lip sync?",
    answer:
      "Dubbing replaces the original audio with a new AI\u2011generated voice in the target language. Lip sync adjusts the translated audio to better match the speaker's mouth movements. This creates a more natural and realistic viewing experience for audiences. You can enable either option depending on your project style and goals. Both features help improve clarity and engagement across languages.",
  },
  {
    question: 'Can I translate multiple videos at once?',
    answer:
      'Yes. FalcoCut allows batch translation, but all videos must share the same resolution for consistent processing. This requirement ensures accurate subtitle timing and smooth dubbing across every file. The system applies identical translation settings to maintain quality across the entire batch. You can process multiple clips efficiently when they follow the same technical specifications. This workflow helps teams handle larger projects without repeating manual steps.',
  },
  {
    question: 'Is there a watermark on free exports?',
    answer:
      'Yes. FalcoCut includes a small watermark on videos exported with the free plan. Upgrading to a paid plan removes all watermarks from your translations. Paid plans also unlock additional minutes and advanced features. This gives you more flexibility for professional or high\u2011volume projects. You can choose the plan that best fits your workflow needs.',
  },
];

// Footer CTA Section
const FOOTER_CTA_CONTENT = {
  title: 'Start Translating Your Spanish Videos into Japanese',
  subtitle:
    'FalcoCut streamlines your video translation workflow with reliable accuracy, secure handling, and automated tools that save hours on every project. Scale your content without scaling your workload.',
  cta: 'Translate Spanish Video',
};

// SEO Config
const SEO_CONFIG: SEOHeadProps = {
  title: 'Translate Spanish Video to Japanese | Online AI Video Translator',
  description:
    "Effortlessly translate your Spanish videos to Japanese with FalcoCut's AI Video Translator. Get fast dubbing, subtitles, and accurate voice translation\u2014all online and easy to use.",
  url: PAGE_URL,
  image: 'https://falcocut.ai/images/og-img.png',
  imageAlt: 'FalcoCut Video Translator - Spanish to Japanese',
  publishedTime: '2026-02-06',
  modifiedTime: '2026-02-06',
  preloadImages: [FEATURE_IMAGES[0]],
  structuredData: [
    createSoftwareAppSchema({
      name: 'FalcoCut Video Translator - Spanish to Japanese',
      description:
        "Effortlessly translate your Spanish videos to Japanese with FalcoCut's AI Video Translator. Get fast dubbing, subtitles, and accurate voice translation\u2014all online and easy to use.",
      url: PAGE_URL,
      applicationCategory: 'MultimediaApplication',
      ratingValue: '4.9',
      ratingCount: '2350',
      featureList: [
        'AI Video Translation',
        'Voice Cloning & Dubbing',
        'Auto Subtitles',
        'Lip Sync',
        'Multi-language Support',
      ],
    }),
    createOrganizationSchema({
      name: 'FalcoCut',
      url: 'https://falcocut.ai',
      logo: 'https://falcocut.ai/images/logo.png',
    }),
    createFAQSchema({
      faqs: FAQS.filter((faq) => faq.answer).map((faq) => ({
        question: faq.question,
        answer: faq.answer as string,
      })),
    }),
    createBreadcrumbSchema({
      items: [
        { name: 'Home', url: 'https://falcocut.ai' },
        {
          name: 'Video Translator',
          url: 'https://falcocut.ai/video-translator',
        },
        { name: 'Spanish to Japanese', url: PAGE_URL },
      ],
    }),
  ],
};

// ============ Page Component ============

const SpanishToJapanesePage: React.FC = () => {
  return (
    <>
      <SEOHead {...SEO_CONFIG} />

      <HeroSection
        title={HERO_CONTENT.title}
        subtitle={HERO_CONTENT.subtitle}
        ctaText={HERO_CONTENT.cta}
        sourceLanguageLabel="Source Language"
        sourceLanguageDefault={HERO_CONTENT.sourceLanguage}
        targetLanguageLabel="Target Language"
        targetLanguageDefault={HERO_CONTENT.targetLanguage}
      />

      <VideoTranslationDemos
        title={DEMOS_CONTENT.title}
        subtitle={DEMOS_CONTENT.subtitle}
        ctaText={DEMOS_CONTENT.cta}
      />

      <FeaturesSection
        title={FEATURES_CONTENT.title}
        subtitle={FEATURES_CONTENT.subtitle}
        features={FEATURES}
        ctaText={FEATURES_CONTENT.cta}
      />

      <HowToSection
        title={HOW_TO_CONTENT.title}
        steps={HOW_TO_STEPS}
        ctaText={HOW_TO_CONTENT.cta}
      />

      <UseCasesSection title={USE_CASES_CONTENT.title} useCases={USE_CASES} />

      <LanguageListSection
        title={LANGUAGE_LIST_CONTENT.title}
        subtitle={LANGUAGE_LIST_CONTENT.subtitle}
        ctaText={LANGUAGE_LIST_CONTENT.cta}
      />

      <WhyChooseSection
        title={WHY_CHOOSE_CONTENT.title}
        subtitle={WHY_CHOOSE_CONTENT.subtitle}
        reasons={WHY_CHOOSE_REASONS}
        ctaText={WHY_CHOOSE_CONTENT.cta}
      />

      <VsSection
        title={VS_CONTENT.title}
        subtitle={VS_CONTENT.subtitle}
        oldTitle={VS_CONTENT.oldTitle}
        oldPoints={VS_CONTENT.oldPoints}
        oldConclusion={VS_CONTENT.oldConclusion}
        newTitle={VS_CONTENT.newTitle}
        newPoints={VS_CONTENT.newPoints}
        newConclusion={VS_CONTENT.newConclusion}
        ctaText={VS_CONTENT.cta}
      />

      <ExploreToolsSection />

      <FAQSection title={FAQ_CONTENT.title} faqs={FAQS} />

      <FooterCTASection
        title={FOOTER_CTA_CONTENT.title}
        subtitle={FOOTER_CTA_CONTENT.subtitle}
        ctaText={FOOTER_CTA_CONTENT.cta}
      />
    </>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {},
    revalidate: 3600,
  };
};

export default withLandingLayout(SpanishToJapanesePage);
